/**
 * CONTRACT FILE. Destination: src/formats/respack/types.ts
 * Implemented by package W1-08 (formats-legacy-respack) as `export const loadRespack: LoadRespackFn`
 * in src/formats/respack/load.ts (validation messages: src/formats/respack/respack.messages.ts, ns 'respack').
 *
 * Phira resource packs (skins), validated EXACTLY like Phira (skin-and-assets.md S§2) so a pack that works
 * in the editor also imports in Phira. Pure: works on bytes; image/audio decoding happens in the renderer
 * and audio engine (render/skin).
 */

import type { MessageRef } from '../../core/util/types';
import type { SkinMetrics } from '../../core/engine/types';

/** info.yml of a respack (camelCase; unknown keys ignored). */
export interface ResPackInfo {
  readonly name: string;
  readonly author: string;
  readonly description: string;
  readonly hitFx: readonly [cols: number, rows: number];
  readonly hitFxDuration: number;
  readonly hitFxScale: number;
  readonly hitFxRotate: boolean;
  readonly hitFxTinted: boolean;
  readonly hideParticles: boolean;
  readonly holdAtlas: readonly [tailPx: number, headPx: number];
  readonly holdAtlasMH: readonly [tailPx: number, headPx: number];
  readonly holdKeepHead: boolean;
  readonly holdRepeat: boolean;
  readonly holdCompact: boolean;
  /** u32: > 0xFFFFFF = ARGB, else RGB opaque. Default 0xe1ffec9f. */
  readonly colorPerfect: number;
  /** Default 0xebb4e1ff. */
  readonly colorGood: number;
}

export const RESPACK_IMAGES = ['click', 'hold', 'flick', 'drag', 'click_mh', 'hold_mh', 'flick_mh', 'drag_mh', 'hit_fx'] as const;
export type RespackImageName = (typeof RESPACK_IMAGES)[number];
/** Optional sounds, looked up as .ogg > .wav > .mp3; `hold` is never used by Phira. */
export const RESPACK_SOUNDS = ['click', 'drag', 'flick', 'ending'] as const;
export type RespackSoundName = (typeof RESPACK_SOUNDS)[number];

export interface RespackFiles {
  readonly info: ResPackInfo;
  /** Raw image bytes keyed by image name (`<name>.png` in the pack, any supported image format inside). */
  readonly images: Readonly<Record<RespackImageName, Uint8Array>>;
  readonly sounds: Partial<Readonly<Record<RespackSoundName, { readonly bytes: Uint8Array; readonly ext: 'ogg' | 'wav' | 'mp3' }>>>;
}

export interface RespackValidation {
  /** Phira's rules: name non-empty and <= 100 UTF-8 bytes, description <= 1000 bytes, 1 <= cols*rows <= 10240,
   *  holdAtlas tail+head < hold.png height (same for MH), all 9 images present and decodable. */
  readonly errors: readonly MessageRef[];
  readonly warnings: readonly MessageRef[];
}

export type LoadRespackFn = (files: ReadonlyMap<string, Uint8Array>) => { readonly pack: RespackFiles | null; readonly validation: RespackValidation };

/** Build SkinMetrics once image sizes are known (renderer decodes images and passes sizes). */
export type SkinMetricsFromFn = (info: ResPackInfo, sizes: Readonly<Record<RespackImageName, readonly [number, number]>>) => SkinMetrics;

/** Phira's colour rule: value > 0xFFFFFF -> ARGB, else RGB with alpha 1. Returns RGBA 0..1. */
export type ParsePhiraColorFn = (v: number) => readonly [number, number, number, number];
